import React, { useState, useMemo, useEffect } from 'react';
import { User, X, Shuffle, Plus, Armchair } from 'lucide-react';
import type { Player, Sport } from '../../types';
import { InjuryBadge } from '../ui/InjuryBadge';
import type { InjuryStatus } from '../../constants/injuries';
import {
  computeGameFieldingPlayerStats,
  emptyFieldingPlayerStats,
  type GameFieldingPlayerStats,
} from '../../utils/gameFieldingPlayerStats';

interface DepthChartEntry {
  id?: string;
  player_id: string | null;
  position: string; // P, C, 1B, 2B, 3B, SS, LF, CF, RF, DH
  depth: number; // Inning number
  color?: string;
}

interface BaseballDiamondFieldingViewProps {
  players: Player[];
  depthChart: DepthChartEntry[];
  sport: Sport;
  activeTab?: string; // Optional, kept for compatibility but not used in baseball
  currentInning: number;
  onPlayerChange: (position: string, inning: number, playerId: string | null) => void;
  playerSortBy: 'firstName' | 'lastName' | 'number';
  onRandomizeClick?: () => void;
  totalInnings: number;
  onAddInning?: () => void;
  /** When true, omits the header Randomize / Add inning buttons (parent can render them). */
  hideHeaderActions?: boolean;
  /**
   * Optional map of player_id -> pitches thrown in this game (raw stat).
   * Surfaced as a small "Px" chip on the pitcher tile when present.
   */
  pitchesByPlayerId?: Map<string, number>;
  /**
   * When true (default), shows the load-management panel summarising
   * innings pitched / caught / sat for each player so coaches can balance
   * tournament-day rotations.
   */
  showRestTracker?: boolean;
}

// Position coordinates on a baseball diamond (as percentages of container)
// These are absolute positions that will be positioned via CSS
// Coordinates assume home plate at bottom center, diamond extending upward

// Mobile coordinates (optimized for mobile view)
const POSITION_COORDS_MOBILE: Record<string, { left: string; top: string; label: string }> = {
  P: { left: '50.5%', top: '55%', label: 'P' },        // Pitcher's mound - center of diamond
  C: { left: '50%', top: '90%', label: 'C' },        // Catcher - behind home plate
  '1B': { left: '95%', top: '52%', label: '1B' },    // First base - right side
  '2B': { left: '70%', top: '30%', label: '2B' },    // Second base - top of diamond, shifted right
  '3B': { left: '5%', top: '52%', label: '3B' },    // Third base - left side
  SS: { left: '32%', top: '30%', label: 'SS' },      // Shortstop - between 2B and 3B
  LF: { left: '8%', top: '18%', label: 'LF' },      // Left field - left outfield
  CF: { left: '50%', top: '5%', label: 'CF' },        // Center field - deep center
  RF: { left: '87%', top: '18%', label: 'RF' },      // Right field - right outfield
};

// Desktop/Tablet coordinates (optimized for larger screens)
const POSITION_COORDS_DESKTOP: Record<string, { left: string; top: string; label: string }> = {
  P: { left: '50%', top: '55%', label: 'P' },        // Pitcher's mound - center of diamond
  C: { left: '50%', top: '90%', label: 'C' },        // Catcher - behind home plate
  '1B': { left: '75%', top: '45%', label: '1B' },    // First base - right side
  '2B': { left: '60%', top: '30%', label: '2B' },    // Second base - top of diamond, shifted right
  '3B': { left: '25%', top: '45%', label: '3B' },    // Third base - left side
  SS: { left: '40%', top: '30%', label: 'SS' },      // Shortstop - between 2B and 3B
  LF: { left: '25%', top: '20%', label: 'LF' },      // Left field - left outfield
  CF: { left: '50%', top: '5%', label: 'CF' },        // Center field - deep center
  RF: { left: '75%', top: '20%', label: 'RF' },      // Right field - right outfield
};

/** Field positions shown on the diamond image (DH is off-diamond, list-only). */
const DIAMOND_POSITION_KEYS = [
  'P',
  'C',
  '1B',
  '2B',
  '3B',
  'SS',
  'LF',
  'CF',
  'RF'
] as const;

export const BaseballDiamondFieldingView: React.FC<BaseballDiamondFieldingViewProps> = ({
  players,
  depthChart,
  sport,
  currentInning,
  onPlayerChange,
  playerSortBy,
  onRandomizeClick,
  totalInnings,
  onAddInning,
  hideHeaderActions = false,
  pitchesByPlayerId,
  showRestTracker = true
}) => {
  const [selectedPosition, setSelectedPosition] = useState<string | null>(null);
  const [showPlayerModal, setShowPlayerModal] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Detect screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768); // 768px is typically the breakpoint for tablet/desktop
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const coordsTable = useMemo(
    () => (isMobile ? POSITION_COORDS_MOBILE : POSITION_COORDS_DESKTOP),
    [isMobile]
  );

  /** Full fielding slot list: diamond positions plus Baseball-only DH (not on diamond). */
  const allPositionKeys = useMemo(
    () => [...DIAMOND_POSITION_KEYS, ...(sport === 'Baseball' ? (['DH'] as const) : [])],
    [sport]
  );

  // Sort players for dropdown
  const sortedPlayers = useMemo(() => {
    return [...players].sort((a, b) => {
      switch (playerSortBy) {
        case 'firstName':
          return a.first_name.localeCompare(b.first_name);
        case 'lastName':
          return a.last_name.localeCompare(b.last_name);
        case 'number':
        default:
          return (a.number || 0) - (b.number || 0);
      }
    });
  }, [players, playerSortBy]);

  // Get player assigned to a position for current inning
  const getPlayerForPosition = (position: string): Player | null => {
    const entry = depthChart.find(
      e => e.position === position && e.depth === currentInning
    );
    if (!entry?.player_id) return null;
    return players.find(p => p.id === entry.player_id) || null;
  };

  // Get players already assigned this inning with their positions
  // Players who appear at least once anywhere in the depth chart — i.e. actually
  // part of this game's lineup. Subs or roster players not assigned to any inning
  // are excluded from bench/unassigned views so the coach only sees active players.
  const inGamePlayerIds = useMemo(
    () =>
      new Set(
        depthChart
          .filter((e) => e.player_id != null)
          .map((e) => e.player_id as string),
      ),
    [depthChart],
  );

  const playersInInning = useMemo(() => {
    const playerPositions = new Map<string, string>();
    depthChart
      .filter(e => e.depth === currentInning && e.player_id)
      .forEach(e => {
        if (e.player_id) {
          playerPositions.set(e.player_id, e.position);
        }
      });
    return playerPositions;
  }, [depthChart, currentInning]);

  // Per-player fielding load metrics (IP, IC, Sat) so coaches can balance
  // pitching, catching, and rest across innings.
  const fieldingPlayerStats = useMemo(
    () =>
      computeGameFieldingPlayerStats({
        depthChart: depthChart.map((e) => ({
          position: e.position,
          depth: e.depth,
          player_id: e.player_id,
        })),
        playerIds: players.map((p) => p.id),
        currentInning,
        pitchesByPlayerId,
      }),
    [depthChart, players, currentInning, pitchesByPlayerId],
  );

  const statsFor = (playerId: string | null | undefined): GameFieldingPlayerStats => {
    if (!playerId) return emptyFieldingPlayerStats();
    return fieldingPlayerStats.get(playerId) ?? emptyFieldingPlayerStats();
  };

  const getPositionLabel = (position: string): string => {
    if (position === 'DH') return 'DH';
    return coordsTable[position]?.label ?? position;
  };

  // Convert inning number to ordinal (1st, 2nd, 3rd, etc.)
  const getInningOrdinal = (inning: number): string => {
    const lastDigit = inning % 10;
    const lastTwoDigits = inning % 100;
    
    if (lastTwoDigits >= 11 && lastTwoDigits <= 13) {
      return `${inning}th`;
    }
    
    switch (lastDigit) {
      case 1: return `${inning}st`;
      case 2: return `${inning}nd`;
      case 3: return `${inning}rd`;
      default: return `${inning}th`;
    }
  };

  const [fieldingView, setFieldingView] = useState<'diamond' | 'list'>('diamond');

  const handlePositionClick = (position: string) => {
    setSelectedPosition(position);
    setShowPlayerModal(true);
  };

  const handlePlayerSelect = (playerId: string | null) => {
    if (selectedPosition) {
      // If selecting a player who is already assigned elsewhere, we need to:
      // 1. Clear their old position
      // 2. Assign them to the new position
      const currentPosition = playerId ? playersInInning.get(playerId) : null;
      
      if (currentPosition && currentPosition !== selectedPosition) {
        // Clear the old position first
        onPlayerChange(currentPosition, currentInning, null);
      }
      
      // Assign to new position (or clear if playerId is null)
      onPlayerChange(selectedPosition, currentInning, playerId);
    }
    setShowPlayerModal(false);
    setSelectedPosition(null);
  };

  return (
    <div className="space-y-4">
      {/* Header with title and buttons */}
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-lg sm:text-xl font-semibold min-w-0">{getInningOrdinal(currentInning)} Inning</h3>
        {!hideHeaderActions && (onRandomizeClick || onAddInning) ? (
          <div className="flex flex-wrap justify-end gap-2 shrink-0">
            {onRandomizeClick && (
              <button
                type="button"
                onClick={onRandomizeClick}
                className="btn-secondary flex items-center gap-1.5 px-3 py-1.5 text-xs"
                title="Build fielding positions across all innings using player cards"
              >
                <Shuffle className="w-3 h-3" />
                Build It
              </button>
            )}
            {onAddInning && (
              <button
                type="button"
                onClick={onAddInning}
                className="btn-secondary flex items-center gap-1.5 px-3 py-1.5 text-xs"
                title={`Add extra inning (currently ${totalInnings} innings)`}
              >
                <Plus className="w-3 h-3" />
                Add Inning ({totalInnings})
              </button>
            )}
          </div>
        ) : null}
      </div>

      {/* Diamond / List toggle */}
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-gray-600">
          {fieldingView === 'diamond'
            ? 'Click on a position on the diamond to assign a player.'
            : 'Click a position row to assign a player.'}
        </p>
        <div className="flex items-center rounded-lg border border-gray-200 p-0.5 shrink-0 bg-gray-50">
          <button
            type="button"
            onClick={() => setFieldingView('diamond')}
            className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
              fieldingView === 'diamond'
                ? 'bg-white shadow text-primary'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            aria-pressed={fieldingView === 'diamond'}
          >
            Diamond
          </button>
          <button
            type="button"
            onClick={() => setFieldingView('list')}
            className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
              fieldingView === 'list'
                ? 'bg-white shadow text-primary'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            aria-pressed={fieldingView === 'list'}
          >
            List
          </button>
        </div>
      </div>

      {/* Baseball Diamond - Container with background image */}
      {fieldingView === 'diamond' && (<>
      <div 
        className="relative w-full max-w-5xl mx-auto bg-center bg-no-repeat overflow-hidden"
        style={{ 
          height: 'clamp(480px, 45vh, 495px)',
          backgroundImage: 'url(/assets/images/baseball-field.jpg)',
          ...(isMobile ? {} : { backgroundSize: '100% 100%' }),
        }}
      >
        {/* Position buttons - absolutely positioned over the field */}
        {DIAMOND_POSITION_KEYS.map(position => {
          const coords = coordsTable[position];
          if (!coords) return null;
          const assignedPlayer = getPlayerForPosition(position);
          
          return (
            <button
              key={position}
              type="button"
              onClick={() => handlePositionClick(position)}
              className="absolute transform -translate-x-1/2 -translate-y-1/2 group z-10"
              style={{
                left: coords.left,
                top: coords.top,
                maxWidth: '12%',
              }}
            >
              <div
                className={`
                  w-10 h-10 sm:w-12 sm:h-12 rounded-full border-2 flex flex-col items-center justify-center
                  transition-all cursor-pointer
                  ${assignedPlayer
                    ? 'bg-primary text-white border-primary shadow-lg ring-1 ring-white'
                    : 'bg-white text-gray-700 border-gray-400 hover:border-primary hover:bg-primary hover:text-white hover:shadow-lg ring-1 ring-white'
                  }
                `}
              >
                {assignedPlayer ? (
                  <>
                    <span className="text-[8px] sm:text-[9px] font-bold leading-tight text-center px-0.5 truncate w-full">
                      {assignedPlayer.last_name}
                    </span>
                    {(assignedPlayer.number || coords.label) && (
                      <span className="text-[7px] sm:text-[8px] font-semibold leading-tight">
                        {assignedPlayer.number ? `#${assignedPlayer.number}` : coords.label}
                      </span>
                    )}
                  </>
                ) : (
                  <span className="text-[10px] sm:text-xs font-bold">{coords.label}</span>
                )}
              </div>
              {/* Tooltip */}
              {assignedPlayer && (
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 hidden group-hover:block z-20 pointer-events-none">
                  <div className="bg-gray-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap shadow-lg">
                    {assignedPlayer.first_name} {assignedPlayer.last_name}
                    {assignedPlayer.number && ` (#${assignedPlayer.number})`}
                  </div>
                </div>
              )}
            </button>
          );
        })}
      </div>

      </>)}

      {/* Position assignments list (includes DH for Baseball — off the diamond).
          Only shown in list view; the diamond handles assignment in diamond view. */}
      {fieldingView === 'list' && <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
        {allPositionKeys.map(position => {
          const assignedPlayer = getPlayerForPosition(position);
          const playerStats = statsFor(assignedPlayer?.id);

          return (
            <div
              key={position}
              className="border border-gray-200 rounded-lg p-3 hover:border-primary transition-colors cursor-pointer"
              onClick={() => handlePositionClick(position)}
            >
              <div className="flex items-center gap-2">
                <span className="font-semibold text-gray-700 shrink-0 pr-5">{position}</span>
                {assignedPlayer ? (
                  <div className="flex items-center gap-2 text-sm min-w-0 flex-1 overflow-hidden">
                    {assignedPlayer.number ? (
                      <span className="font-semibold text-primary shrink-0">#{assignedPlayer.number}</span>
                    ) : (
                      <User size={14} className="text-gray-400 flex-shrink-0" />
                    )}
                    <div className="flex items-center gap-1 min-w-0 flex-1 overflow-hidden">
                      <span className="text-gray-700 truncate text-left min-w-0">
                        {assignedPlayer.first_name} {assignedPlayer.last_name}
                      </span>
                      {assignedPlayer.injury_status && assignedPlayer.injury_status !== 'H' && (
                        <InjuryBadge
                          injuryStatus={assignedPlayer.injury_status as InjuryStatus}
                          size="sm"
                          className="flex-shrink-0"
                        />
                      )}
                    </div>
                    {/* Position-specific load indicators help coaches manage
                        pitcher / catcher rotations during tournament days. */}
                    {position === 'P' && (
                      <div className="flex items-center gap-1 shrink-0">
                        <span
                          className="inline-flex items-center rounded-full bg-amber-100 text-amber-800 px-1.5 py-0.5 text-[10px] font-semibold"
                          title={`Innings pitched so far: ${playerStats.inningsPitched}`}
                          aria-label={`Innings pitched ${playerStats.inningsPitched}`}
                        >
                          IP {playerStats.inningsPitched}
                        </span>
                        {playerStats.pitchesThrown != null && (
                          <span
                            className="inline-flex items-center rounded-full bg-amber-50 text-amber-700 px-1.5 py-0.5 text-[10px] font-semibold border border-amber-200"
                            title={`Pitches thrown this game: ${playerStats.pitchesThrown}`}
                            aria-label={`Pitches thrown ${playerStats.pitchesThrown}`}
                          >
                            P {playerStats.pitchesThrown}
                          </span>
                        )}
                      </div>
                    )}
                    {position === 'C' && (
                      <span
                        className="inline-flex items-center rounded-full bg-sky-100 text-sky-800 px-1.5 py-0.5 text-[10px] font-semibold shrink-0"
                        title={`Innings caught so far: ${playerStats.inningsCaught}`}
                        aria-label={`Innings caught ${playerStats.inningsCaught}`}
                      >
                        IC {playerStats.inningsCaught}
                      </span>
                    )}
                  </div>
                ) : (
                  <span className="text-sm text-gray-400">Click to assign</span>
                )}
              </div>
            </div>
          );
        })}
      </div>}

      {/* Show unassigned players, annotated with how often each has already sat.
          Only players who appear somewhere in the depth chart are shown — subs
          and roster players not in the lineup are excluded. */}
      {players.filter(p => inGamePlayerIds.has(p.id) && !playersInInning.has(p.id)).length > 0 && (
        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
          <h4 className="text-sm font-semibold text-gray-700 mb-2">
            Unassigned Players (Inning {currentInning})
          </h4>
          <div className="flex flex-wrap gap-2">
            {players
              .filter(p => inGamePlayerIds.has(p.id) && !playersInInning.has(p.id))
              .map(player => {
                const s = statsFor(player.id);
                return (
                  <span
                    key={player.id}
                    className="inline-flex items-center gap-1 text-sm text-gray-600 px-2 py-1 bg-white rounded border border-gray-200"
                  >
                    {player.number ? `#${player.number} ` : ''}
                    {player.first_name} {player.last_name}
                    {s.inningsSat > 0 && (
                      <span
                        className="inline-flex items-center gap-0.5 rounded-full bg-rose-100 text-rose-800 px-1.5 py-0.5 text-[10px] font-semibold"
                        title={`Innings already sat: ${s.inningsSat}`}
                        aria-label={`Sat out ${s.inningsSat} previous innings`}
                      >
                        <Armchair size={10} aria-hidden />
                        {s.inningsSat}
                      </span>
                    )}
                  </span>
                );
              })}
          </div>
        </div>
      )}

      {/* Rest / load tracker so coaches can balance pitching, catching, and
          bench time across innings — especially useful on tournament days. */}
      {showRestTracker && players.length > 0 && (
        <div className="mt-4 border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
            <h4 className="text-sm font-semibold text-gray-700">Rest &amp; Load Tracker</h4>
            <span className="text-xs text-gray-500">
              Through inning {currentInning} of {totalInnings}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="text-left font-medium px-3 py-2">Player</th>
                  <th
                    className="text-center font-medium px-2 py-2"
                    title="Innings pitched (P) so far this game"
                  >
                    IP
                  </th>
                  <th
                    className="text-center font-medium px-2 py-2"
                    title="Innings caught (C) so far this game"
                  >
                    IC
                  </th>
                  <th
                    className="text-center font-medium px-2 py-2"
                    title="Innings already sat out (excludes the current inning)"
                  >
                    Sat
                  </th>
                  <th
                    className="text-center font-medium px-2 py-2"
                    title="Pitch count for pitchers; estimated pitches received for catchers"
                  >
                    Pitches
                  </th>
                </tr>
              </thead>
              <tbody>
                {[...players].filter((p) => inGamePlayerIds.has(p.id))
                  .sort((a, b) => {
                    const sa = statsFor(a.id);
                    const sb = statsFor(b.id);
                    if (sb.inningsSat !== sa.inningsSat) return sb.inningsSat - sa.inningsSat;
                    return (a.number || 0) - (b.number || 0);
                  })
                  .map((player, idx) => {
                    const s = statsFor(player.id);
                    // Pitches column: thrown (pitcher) or caught (catcher)
                    const pitchDisplay =
                      s.pitchesThrown != null
                        ? s.pitchesThrown
                        : s.pitchesCaught != null
                        ? s.pitchesCaught
                        : null;
                    return (
                      <tr
                        key={player.id}
                        className={
                          'border-t border-gray-100 ' +
                          (idx % 2 === 0 ? 'bg-white' : 'bg-gray-50')
                        }
                      >
                        <td className="px-3 py-1.5 text-gray-800">
                          {player.number ? (
                            <span className="font-semibold text-primary mr-1">#{player.number}</span>
                          ) : null}
                          {player.first_name} {player.last_name}
                        </td>
                        <td className="px-2 py-1.5 text-center tabular-nums">
                          {s.inningsPitched || ''}
                        </td>
                        <td className="px-2 py-1.5 text-center tabular-nums">
                          {s.inningsCaught || ''}
                        </td>
                        <td className="px-2 py-1.5 text-center tabular-nums">
                          {s.inningsSat ? (
                            <span className="inline-flex items-center gap-0.5 rounded-full bg-rose-100 text-rose-800 px-1.5 py-0.5 text-[11px] font-semibold">
                              <Armchair size={11} aria-hidden />
                              {s.inningsSat}
                            </span>
                          ) : (
                            ''
                          )}
                        </td>
                        <td className="px-2 py-1.5 text-center tabular-nums text-gray-700">
                          {pitchDisplay != null ? pitchDisplay : <span className="text-gray-300">—</span>}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Player Selection Modal */}
      {showPlayerModal && selectedPosition && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">
                Select Player for {getPositionLabel(selectedPosition)}
              </h3>
              <button
                onClick={() => {
                  setShowPlayerModal(false);
                  setSelectedPosition(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              <div className="space-y-2">
                <button
                  onClick={() => handlePlayerSelect(null)}
                  className="w-full text-left px-4 py-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <span className="text-gray-600">Clear Position</span>
                </button>

                {sortedPlayers.map(player => {
                  const currentPosition = playersInInning.get(player.id);
                  const isCurrentlyAtThisPosition = getPlayerForPosition(selectedPosition)?.id === player.id;
                  const isAssignedElsewhere = currentPosition && currentPosition !== selectedPosition;
                  const s = statsFor(player.id);
                  /** Surface IP/IC chips only when picking the matching position
                      so we don't clutter outfield assignments with pitcher data. */
                  const showIP = selectedPosition === 'P' && s.inningsPitched > 0;
                  const showIC = selectedPosition === 'C' && s.inningsCaught > 0;
                  const showSat = s.inningsSat > 0;
                  const chipBaseClass = isCurrentlyAtThisPosition
                    ? 'bg-white/20 text-white border border-white/30'
                    : '';
                  return (
                    <button
                      key={player.id}
                      onClick={() => handlePlayerSelect(player.id)}
                      className={`
                        w-full text-left px-4 py-3 border rounded-lg transition-colors
                        ${isCurrentlyAtThisPosition
                          ? 'bg-primary text-white border-primary'
                          : isAssignedElsewhere
                          ? 'border-yellow-300 bg-yellow-50 hover:bg-primary hover:text-white hover:border-primary'
                          : 'border-gray-200 hover:bg-primary hover:text-white hover:border-primary'
                        }
                      `}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          {player.number ? (
                            <span className="font-semibold">#{player.number}</span>
                          ) : (
                            <User size={16} className="text-gray-400" />
                          )}
                          <span className="truncate">
                            {player.first_name} {player.last_name}
                          </span>
                          {player.injury_status && player.injury_status !== 'H' && (
                            <InjuryBadge injuryStatus={player.injury_status as InjuryStatus} size="sm" />
                          )}
                          <div className="flex items-center gap-1 ml-auto">
                            {showIP && (
                              <span
                                className={
                                  chipBaseClass ||
                                  'inline-flex items-center rounded-full bg-amber-100 text-amber-800 px-1.5 py-0.5 text-[10px] font-semibold'
                                }
                                title={`Innings pitched: ${s.inningsPitched}`}
                              >
                                IP {s.inningsPitched}
                              </span>
                            )}
                            {showIC && (
                              <span
                                className={
                                  chipBaseClass ||
                                  'inline-flex items-center rounded-full bg-sky-100 text-sky-800 px-1.5 py-0.5 text-[10px] font-semibold'
                                }
                                title={`Innings caught: ${s.inningsCaught}`}
                              >
                                IC {s.inningsCaught}
                              </span>
                            )}
                            {showSat && (
                              <span
                                className={
                                  chipBaseClass ||
                                  'inline-flex items-center gap-0.5 rounded-full bg-rose-100 text-rose-800 px-1.5 py-0.5 text-[10px] font-semibold'
                                }
                                title={`Sat out ${s.inningsSat} previous innings`}
                              >
                                <Armchair size={10} aria-hidden />
                                {s.inningsSat}
                              </span>
                            )}
                          </div>
                        </div>
                        {isAssignedElsewhere && (
                          <span className="text-xs font-medium shrink-0">
                            Currently: {getPositionLabel(currentPosition)}
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

